/* ============================================================
   ABOUT PAGE — two halves, same shape as every other page here.

     1. The HEADER, which is navigation and must not wait on a CDN.
     2. DECORATION, behind an `if(gsap)` guard.

   There is no commerce on this page: no price, no variant, no cart
   line. It is words and photographs, so there is no Shopify call and
   nothing here can fail in a way that costs a sale. If the CDN never
   answers, the page is the same page and it does not move.
   ============================================================ */

import { initHeader } from "./header.js";
import { splitWords } from "./split.js";
import * as scenes    from "./scenes.js";

const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

/* Before anything touches GSAP. The bar holds the only route to the
   cart from here, and it must be there whether or not a CDN was. */
initHeader();


/* ============================================================
   DECORATION — optional, all of it
   ============================================================ */

const { gsap, ScrollTrigger, Lenis } = window;

if(gsap && ScrollTrigger){
  gsap.registerPlugin(ScrollTrigger);
  gsap.config({ force3D: true });
  ScrollTrigger.config({ ignoreMobileResize: true });

  const REDUCED = matchMedia("(prefers-reduced-motion:reduce)").matches;

  if(Lenis && !REDUCED){
    const lenis = new Lenis({
      duration: 1.15,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.7
    });
    // Published so the side menu can park the scroll loop while the
    // drawer is open.
    window.lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }

  /* ---------- the headline ----------
     Word by word out of its masks, the same beat the catalogue page
     and the product steps use. No delay and no scroll trigger: it is
     the first thing on the page and there is no loader here to wait
     for — this page is reached from somewhere else on the site, and
     someone who has just clicked "About" should see the answer, not
     an intro.

     gsap.set for the hidden state rather than a `from`, so text that
     is never animated — because this whole block was skipped — is
     readable rather than parked below its mask.

     ⚠ splitWords REBUILDS the element from its textContent, which
     destroys the <em> carrying the cyan on "crafting true Indian
     products". So the accent is measured BEFORE the split and put
     back on the words it covered — the word-level version of what
     tintRange does for characters on the homepage headline. Without
     this the headline quietly loses its colour the moment GSAP
     arrives, which is the sort of bug that only shows up on a fast
     connection. */
  const h1 = $(".ab-hero h1");
  if(h1 && !REDUCED){
    const accent = h1.querySelector("em")?.textContent.trim() ?? "";
    const at = accent ? h1.textContent.indexOf(accent) : -1;
    // Words before the accent starts, and words the accent covers.
    const skip = at > -1 ? h1.textContent.slice(0, at).trim().split(/\s+/).filter(Boolean).length : -1;
    const span = accent ? accent.split(/\s+/).length : 0;

    const words = splitWords(h1);
    if(skip > -1){
      for(let i = skip; i < skip + span && i < words.length; i++){
        words[i].style.color = "var(--cyan)";
      }
    }

    gsap.set(words, { yPercent: 118 });
    gsap.to(words, { yPercent: 0, duration: 1, ease: "expo.out", stagger: .045 });
  }
  gsap.from(".ab-hero .eyebrow, .ab-hero-r", {
    opacity: 0, y: 18, duration: 1, ease: "expo.out", stagger: .1, delay: .15
  });

  /* ---------- the story beats ----------
     Each on its OWN trigger rather than one stagger down the panel.
     Stacked on a phone these are a screen apart, and a single
     trigger would spend the whole animation on beats nobody can see
     yet. The text and the picture come in together but from
     opposite sides, which is what makes the alternating layout read
     as deliberate. */
  for(const beat of $$(".ab-beat")){
    const flip = beat.classList.contains("ab-beat--flip");
    const from = { scrollTrigger: { trigger: beat, start: "top 82%", once: true } };

    gsap.from(beat.querySelector(".ab-beat-txt"), {
      ...from, opacity: 0, x: flip ? 34 : -34, duration: 1.1, ease: "expo.out"
    });
    gsap.from(beat.querySelector(".ab-beat-img"), {
      ...from, opacity: 0, x: flip ? -34 : 34, y: 20, duration: 1.1, ease: "expo.out", delay: .08
    });
  }

  /* Headings and the two HomeGround columns: a plain rise. They are
     centred blocks, so there is no side for them to come in from. */
  for(const el of $$(".ab-calling, .ab-story-head, .ab-ground > h2, .ab-ground-item, .ab-advisor-l, .ab-advisor-r, .ab-strip-head")){
    gsap.from(el, {
      opacity: 0, y: 26, duration: 1, ease: "expo.out",
      scrollTrigger: { trigger: el, start: "top 88%", once: true }
    });
  }

  /* ---------- the India tag ----------
     The line the page is built to arrive at, so it gets the one beat
     nothing else on the page has: it scales up very slightly as it
     lands. Small on purpose — 1.06 to 1 reads as emphasis, anything
     more reads as a pop-up. */
  const tag = $(".ab-tag h2");
  if(tag){
    gsap.from(tag, {
      opacity: 0, y: 30, scale: 1.06, duration: 1.3, ease: "expo.out",
      scrollTrigger: { trigger: ".ab-tag", start: "top 85%", once: true }
    });
  }

  /* ---------- the milestone strip ----------
     It starts scrolled a little way in, so the fact that it scrolls
     is visible rather than something you have to guess at — the left
     edge of the artwork is flush with the gutter otherwise and it
     looks like a picture that has simply been cropped.

     Only where it actually overflows: on a wide screen the whole
     strip fits and nudging it would just hide the first photograph.
     `instant` behaviour because this is layout, not a gesture. */
  const strip = $(".ab-strip-scroll");
  if(strip){
    addEventListener("load", () => {
      const over = strip.scrollWidth - strip.clientWidth;
      if(over > 40) strip.scrollTo({ left: Math.min(over, 60), behavior: "instant" });
    });
  }

  scenes.marquee(gsap);
  scenes.footerCta(gsap);

  // The photographs decide the height of half this page, and
  // measuring before they have decoded puts every start position out.
  addEventListener("load", () => ScrollTrigger.refresh());
}
